import React, { useEffect, useMemo, useState, useRef } from "react";
import { Box, Button, Container, Flex, Text, TextInput, Modal} from "@mantine/core";
import { MantineReactTable } from "mantine-react-table";
import { IconEye } from "@tabler/icons-react";
import { useQuery } from "react-query";

import { coupons_api } from "./coupons.service";
import AppBreadcrumb from "../../../components/AppBreadcrumb";
import { showSuccessToast, showErrorToast } from "../../../utilities/Toast";



export default function YearlyCoupon() {
  const [data, setData] = useState([]);
  const [viewMode, setViewMode] = useState("YEARLY");
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [cutoffValue, setCutoffValue] = useState("");
  const cutoffInputRef = useRef(null);
  const [viewData, setViewData] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [avgValue, setAvgValue] = useState(null);

  useEffect(() => {
    if (editData) {
      // small delay to ensure modal is rendered
      setTimeout(() => {
        cutoffInputRef.current?.focus();
      }, 100);
    }
  }, [editData]);




  // =========================
  // FETCH YEARLY COUPON LIST
  // =========================
  const yearlyCouponQuery = useQuery(
    ["yearly-coupon"],
    () => coupons_api.yearlyCouponGet(),
    {
      enabled: viewMode === "YEARLY",
      onSuccess: (res) => {
        setData(res?.data?.results || []);
      },
    }
  );


  const fetchMonthlyUserPurchase = async () => {
    try {
      setLoading(true);
      const res = await coupons_api.monthlyUserPurchaseGet();
      if (res?.status) {
        setData(res.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const updateCutoffAmount = async () => {
    if (!editData) return;

    const res = await coupons_api.updateMonthlyCutoff(editData.id, {
      cutt_off_amount: cutoffValue,
    });

    if (res?.status) {
      const userName = editData.username || "-";
      const currentMonthAmount =
        editData.currentmonth_total !== null &&
          editData.currentmonth_total !== undefined
          ? `$${Number(editData.currentmonth_total).toFixed(2)}`
          : "-";

      showSuccessToast({
        title: "Cutoff Updated",
        message: `User: ${userName} | Current Month: ${currentMonthAmount}`,
      });

      setEditData(null);
      setCutoffValue("");
      fetchMonthlyUserPurchase();
    } else {
      showErrorToast({
        title: "Error",
        message: "Update failed",
      });
    }
  };



  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return `₹ ${Number(value).toFixed(2)}`;
  };

  const openEditModal = (row) => {
    setEditData(row);
    setCutoffValue(row.cutt_off_amount || "");
  };


  // =========================
  // monnthlyuserpurcahse TABLE COLUMNS
  // =========================

  const monthlyColumns = useMemo(
    () => [
      {
        header: "User",
        accessorKey: "username",
        size:"auto",

      },
      {
        header: "Address",
        accessorKey: "address_line_1",
      },
      {
        header: "Current Month Total",
        accessorKey: "currentmonth_total",
        Cell: ({ cell }) => (
          <Text>{formatAmount(cell.getValue())}</Text>
        ),
      },
      {
        header: "Monthly Max Amt",
        accessorKey: "monthly_avg_amount",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          
          if (value === null || value === undefined || value === "") {
            return <Text>-</Text>;
          }
          
          const isAboveLimit = Number(value) >= 5000;
          
          return (
            <Text fw={600} c={isAboveLimit ? "green" : "red"}>
              {formatAmount(value)}
            </Text>
          );
        },
      },
      
      {
        header: "Max Amt Month",
        accessorKey: "max_month_amt",
      },
      {
        header: "Cutoff Amount",
        accessorKey: "cutt_off_amount",
        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!value) {
            return <Text>-</Text>;
          }

          const isAboveLimit = Number(value) >= 5000;

          return (
            <Text fw={600} c={isAboveLimit ? "green" : "red"}>
              {formatAmount(value)}
            </Text>
          );
        },
      },

      {
  header: "Actions",
  accessorKey: "id",
  Cell: ({ row }) => (
    <Flex gap="xs">
      <Button
        size="xs"
        variant="light"
        onClick={() => openEditModal(row.original)}
      >
        Edit
      </Button>

      <Button
      size="xs"
      variant="light"
      onClick={() => setViewData(row.original)}
    >
      <IconEye size={16} />
      </Button>
    </Flex>
  ),
},
    ],
    []
  );




  // =========================
  // Yearly TABLE COLUMNS
  // =========================
  const columns = useMemo(
    () => [
      {
        header: "User",
        accessorKey: "username",
      },
      {
        header: "Month Count",
        accessorKey: "month_count",
      },
      {
        header: "Coupon Code",
        accessorKey: "coupon_code",
        Cell: ({ cell }) => (
          <Text fw={600}>{cell.getValue() || "-"}</Text>
        ),
      },
      {
        header: "Coupon Qty",
        accessorKey: "coupon_qty",
      },

      {
  header: "Status",
  accessorKey: "status",
  Cell: ({ cell }) => {
    const value = cell.getValue();

    let color = "gray";

    if (value === "Used") color = "blue";
    else if (value === "Expired");
    else if (value === "Active") color = "green";
    else if (value === "Inactive") color = "red";

    return (
      <Text c={color}>
        {value}
      </Text>
    );
  },
},
    ],
    []
  );

  // =========================
  // UI
  // =========================
  return (
    <Container fluid>
   <Box px={25} py={15}>
  <Flex justify="space-between" align="end">
    {/* LEFT */}
    <Box mt={13}>
      <Text size="lg" fw={600}>
        {viewMode === "YEARLY"
          ? "Yearly Coupons"
          : "Monthly Customer Purchase"}
      </Text>

      <Box mt={6}>
        <AppBreadcrumb />
      </Box>
    </Box>

   
    <Flex gap="sm"  mt={10}>
      {viewMode === "MONTHLY" && (
        <Button
          size="md"
          variant="outline"
          onClick={() => {
            setViewMode("YEARLY");
            yearlyCouponQuery.refetch();
          }}
        >
          Back
        </Button>
      )}

      <Button
        size="md"
        onClick={() => {
          setViewMode("MONTHLY");
          fetchMonthlyUserPurchase();
        }}
      >
        Set Cutoff
      </Button>
    </Flex>
  </Flex>
</Box>




      <Box px={25} mt={12}>
        <Box
          sx={{
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >


          {/* TABLE */}
          <MantineReactTable
            columns={viewMode === "YEARLY" ? columns : monthlyColumns}
            data={data}
            state={{
              isLoading:
                viewMode === "YEARLY"
                  ? yearlyCouponQuery.isLoading
                  : loading,
            }}
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableGlobalFilter={false}
            initialState={{ density: "xs" }}

            /* 🔥 THIS IS THE KEY PART */
            renderTopToolbarCustomActions={() => (
              <Box px={24} pt={14} pb={14}>
                <Text
                  fw={600}
                  tt="uppercase"
                  size="sm"
                  sx={(theme) => ({
                    letterSpacing: 1,
                    color: theme.colors.brand[7],
                  })}
                >
                  {viewMode === "YEARLY"
                    ? "Yearly Coupons List"
                    : "Monthly Customer Purchase List"}
                </Text>
              </Box>
            )}
          />

        </Box>
      </Box>

      <Modal
        opened={!!editData}
        onClose={() => setEditData(null)}
        title="Edit Cutoff Amount"
        centered
        withinPortal={false}
      >
        {editData && (
          <Box
            mb="md"
            p="sm"
            sx={{
              backgroundColor: "#f8f9fa",
              borderRadius: 6,
              border: "1px solid #e9ecef",
            }}
          >
            <Flex justify="space-between" mb={6}>
              <Text size="sm" fw={500} c="dimmed">
                User
              </Text>
              <Text size="sm" fw={600}>
                {editData.username}
              </Text>
            </Flex>

            <Flex justify="space-between">
              <Text size="sm" fw={500} c="dimmed">
                Monthly Max Amt
              </Text>
              <Text size="sm" fw={600} c="green">
                {formatAmount(editData.monthly_avg_amount)}
              </Text>
            </Flex>
          </Box>
        )}

        <TextInput
          ref={cutoffInputRef}
          label="Cutoff Amount"
          value={cutoffValue}
          onChange={(e) => setCutoffValue(e.target.value)}
          type="number"
          min={0}
          mb="md"
        />

        <Flex justify="flex-end">
          <Button size="sm" color="green" onClick={updateCutoffAmount}>
            Update
          </Button>
        </Flex>
      </Modal>
     <Modal
  opened={!!viewData}
  onClose={() => {
    setViewData(null);
    setSelectedMonths([]);
    setAvgValue(null);
  }}
  title="Last 10 Months Purchase"
  centered
  size="md"
  withinPortal={false}
>
{viewData && viewData.last_ten_months?.length > 0 && (() => {

  const months = viewData.last_ten_months;

  // select / unselect month
  const toggleMonth = (item) => {

    const exists = selectedMonths.find(m => m.month === item.month);

    if (exists) {
      setSelectedMonths(selectedMonths.filter(m => m.month !== item.month));
    } else {
      setSelectedMonths([...selectedMonths, item]);
    }
  };

  // calculate average
  const calculateAverage = () => {

    if (selectedMonths.length === 0) return;

    const total = selectedMonths.reduce(
      (sum, m) => sum + Number(m.total),
      0
    );

    const avg = total / selectedMonths.length;

    setAvgValue(avg);
  };

  return (
    <>

      {/* Top Box */}
      <Box
        mb="md"
        p="md"
        sx={{
          background: "linear-gradient(90deg, #e6fcf5, #d3f9d8)",
          borderRadius: 8,
          border: "1px solid #38d9a9",
        }}
      >
        <Text size="sm" c="dimmed" mb={4}>
          Selected Months Average
        </Text>

        <Flex justify="space-between" align="center">
          <Text fw={600}>
            {selectedMonths.length} Months Selected
          </Text>

          <Text fw={700} c="green">
            ₹ {avgValue ? Number(avgValue).toLocaleString() : 0}
          </Text>
        </Flex>
      </Box>

      {/* Month List */}
      <Text size="sm" fw={600} mb={6}>
        Monthly Breakdown
      </Text>

      {months.map((item, index) => {

        const isSelected = selectedMonths.find(m => m.month === item.month);

        return (
          <Flex
            key={index}
            justify="space-between"
            mb={6}
            p="xs"
            onClick={() => toggleMonth(item)}
            sx={{
              cursor: "pointer",
              backgroundColor: isSelected ? "#e6fcf5" : "#f8f9fa",
              borderRadius: 6,
              border: isSelected ? "1px solid #20c997" : "none",
            }}
          >
            <Text size="sm">{item.month}</Text>

            <Text size="sm" fw={600}>
              ₹ {Number(item.total).toLocaleString()}
            </Text>
          </Flex>
        );
      })}

      {/* AVG Button */}
      <Button
        fullWidth
        mt="md"
        color="green"
        onClick={calculateAverage}
      >
        AVG
      </Button>

    </>
  );
})()}
</Modal>

    </Container>



  );


}
